import React, { FC, useCallback } from 'react'

// Constants
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'

// Interfaces
import Image from '@src/quince-site-common/src/domains/Image'
import RichText from '@src/quince-site-common/src/domains/RichText'
import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'

// Components
import { FeatureSectionFour } from '@src/quince-components/src'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'

// Styles
import { isTextNode } from '@src/quince-site-common/src/utils/typeGuards'

import styles from './imageWithScale.module.scss'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { computeScrollPercentage } from '@src/quince-site-common/src/hooks/useScrollEventTracker'
import TrackingEvent from '@src/quince-site-common/src/helpers/tracking-event/tracking-event'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'

const ImageWithScale = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const product = 'bundle' in props ? props.bundle : props.product
  const desktopSectionDetails = product.desktopSections.find(
    ds => ds.sectionType === CUSTOM_SECTION_TYPE.FEATURE_SECTION_4,
  )
  const mobileSectionDetails = product.mobileSections.find(
    ds => ds.sectionType === CUSTOM_SECTION_TYPE.FEATURE_SECTION_4,
  )

  return (
    <>
      {desktopSectionDetails ? (
        <div className="desktopOnly">
          <RenderSection sectionDetails={desktopSectionDetails} />
        </div>
      ) : null}

      {mobileSectionDetails ? (
        <div className="mobileOnly">
          <RenderSection sectionDetails={mobileSectionDetails} />
        </div>
      ) : null}
    </>
  )
}

const RenderSection: FC<{ sectionDetails: CustomSection }> = ({ sectionDetails }) => {
  const contentSubSection = sectionDetails.subSections?.find(
    ss => ss.sectionType === CUSTOM_SECTION_TYPE.TEXT_WITH_IMAGE,
  )
  const rangeBarSubSection = sectionDetails.subSections?.find(
    ss => ss.sectionType === CUSTOM_SECTION_TYPE.FILL_RANGE_BAR,
  )

  // Background color for feature section
  const { className: contentfulFeatureSectionTwoClassName, styleObject: contentfulFeatureSectionTwoStyles } =
    useDynamicStyle({
      baseClassNames: sectionDetails.classNames,
      mobileClassNamesOverride: sectionDetails.classNamesMobileOverrides,
      dynamicValueClassesMobileOverride: sectionDetails.mobileDynamicClassesOverrides,
      baseDynamicValueClasses: sectionDetails.baseDynamicClasses,
      postFix: styles.dynamicClassesPostFix || '',
    })

  const { className: subSectionClassName, styleObject: subSectionStyle } = useDynamicStyle({
    baseClassNames: contentSubSection?.classNames,
    mobileClassNamesOverride: contentSubSection?.classNamesMobileOverrides,
    dynamicValueClassesMobileOverride: contentSubSection?.mobileDynamicClassesOverrides,
    baseDynamicValueClasses: contentSubSection?.baseDynamicClasses,
    postFix: 'subsection',
  })

  // Fill color for the range bar
  const { className: contentfulFillBarClassName, styleObject: contentfulFillBarStyles } = useDynamicStyle({
    baseClassNames: rangeBarSubSection?.richTexts?.[2]?.commonClassNames,
    mobileClassNamesOverride: rangeBarSubSection?.richTexts?.[2]?.mobileClassNames,
    dynamicValueClassesMobileOverride: rangeBarSubSection?.richTexts?.[2]?.dynamicValueClassesMobile,
    baseDynamicValueClasses: rangeBarSubSection?.richTexts?.[2]?.baseDynamicValueClasses,
    postFix: 'fill-bar', // TODO: Setup generic classname
  })

  const getIsVisibleTrackParams = useCallback(() => {
    return {
      action: 'feature_section_two_viewed',
      value: true,
      context: {
        contentContainerType: sectionDetails.contentContainerType,
        contentContainerName: sectionDetails.contentContainerName,
        trackingId: sectionDetails.trackingId,
        contentContainerDisplayName: sectionDetails.displayTitle,
        is_in_first_fold: computeScrollPercentage() === 0,
      },
    }
  }, [sectionDetails])
  const shouldTrackVisible =
    sectionDetails.trackingId &&
    sectionDetails.trackingTypes?.includes(TrackingEvent.TRACKING_BROWSER_EVENT_TYPES.VISIBLE)

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(shouldTrackVisible ? getIsVisibleTrackParams : () => null)

  return (
    <>
      <FeatureSectionFour
        ref={trackIsVisibleRef}
        sectionImage={Image.makeImageProps(sectionDetails.images?.[0])}
        fillRange={
          rangeBarSubSection
            ? {
                fillPercentage: Number(
                  isTextNode(rangeBarSubSection.richTexts?.[2]?.raw.content?.[0]?.content[0])
                    ? rangeBarSubSection.richTexts[2].raw.content[0].content[0].value
                    : 0,
                ),
                minLabel: isTextNode(rangeBarSubSection.richTexts?.[0]?.raw?.content?.[0]?.content[0])
                  ? rangeBarSubSection.richTexts[0].raw.content[0].content[0].value || ''
                  : '',
                maxLabel: isTextNode(rangeBarSubSection.richTexts?.[1]?.raw?.content?.[0]?.content[0])
                  ? rangeBarSubSection.richTexts[1].raw.content[0].content[0].value || ''
                  : '',
                fillColor: '#21201F',
                rounded: true,
                border: true,
              }
            : undefined
        }
        iconProps={Image.makeImageProps(contentSubSection?.images?.[0])}
        heading={RichText.makeRichTextProps(contentSubSection?.richTexts?.[0])}
        description={RichText.makeRichTextProps(contentSubSection?.richTexts?.[1])}
        className={contentfulFeatureSectionTwoClassName}
        style={contentfulFeatureSectionTwoStyles}
        fillBarClassName={contentfulFillBarClassName}
        fillBarStyle={{ ...contentfulFillBarStyles }}
        subSectionClassName={subSectionClassName}
        subSectionStyle={subSectionStyle}
      />
    </>
  )
}

export default ImageWithScale
